import { RawDynamicForm } from 'components/dynamicForm/DynamicForm';
import {
  DynamicFormDispatchProps,
  DynamicFormOwnProps,
  DynamicFormStateProps,
} from 'components/dynamicForm/DynamicForm.connect';
import { connect } from 'react-redux';
import type { QuoteFormError } from 'state/addonForm/addonForm.selector';
import { dynamicFormTabRollbackedThunk, workspaceTabClonedThunk } from 'state/dynamic/dynamicForm.thunks';
import { isDynamicFormLoaded } from 'state/dynamic/selectors';
import type { DataById } from 'state/formData/formData.model';
import {
  formInputDataChangedThunk,
  refreshScheduleThunk,
  resetDefaultDatesThunk,
} from 'state/formData/formData.thunks';
import { getBlocksMetadata, getFieldsMetadata, getProductDescription } from 'state/forms/metadata/selectors';
import * as ProductModificationSelectors from 'state/productModificationForm/productModificationForm.selector';
import {
  getDynamicFormInfos,
  getDynamicFormValidationErrors,
  getFormEnrichedData,
  getFormInputData,
  getFormPendingEnrichmentData,
  getIsUserInternal,
  isPendingEnrichment,
} from 'state/selectors';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import { getTabIsFrozen } from 'state/workspaces/tabs/tabs.selector';
import { workspaceResetTabRequested } from 'state/workspaces/workspace.actions';

interface StateProps extends DynamicFormStateProps {
  pendingEnrichmentData: DataById;
  validationErrors: readonly QuoteFormError[];
  isPerformingEnrichment: boolean;
}

export type DynamicFormProps = DynamicFormOwnProps & StateProps & DynamicFormDispatchProps;
const EMPTY_OBJECT = {} as const;
const mapStateToProps = (state: AppState, { formId }: DynamicFormOwnProps): StateProps => {
  const { crossBrowserDirty = false, negotiationId } = getDynamicFormInfos(state, formId) ?? {};
  const productDescription = getProductDescription(state, formId);
  const blocksMetadata = getBlocksMetadata(state, 'dynamic', formId) ?? EMPTY_OBJECT;
  const fieldsMetadata = getFieldsMetadata(state, 'dynamic', formId) ?? EMPTY_OBJECT;
  const enrichedData = getFormEnrichedData(state, formId);
  const inputData = getFormInputData(state, formId);
  const pendingEnrichmentData = getFormPendingEnrichmentData(state, formId);
  const isLoading = !isDynamicFormLoaded(state, formId);
  const isQuoting = ProductModificationSelectors.hasAppliedModification(state, formId);
  const validationErrors = getDynamicFormValidationErrors(state, formId);
  const isUserInternal = getIsUserInternal(state);
  const isTabFrozen = getTabIsFrozen(state, formId) ?? false;
  const isPerformingEnrichment = isPendingEnrichment(state, formId);
  const canResetOrClone = !isLoading && !isPerformingEnrichment && !isQuoting && !isTabFrozen;
  const isNotEditable = isTabFrozen || crossBrowserDirty;
  const canReloadSession = !isTabFrozen;

  return {
    productDescription,
    blocksMetadata,
    fieldsMetadata,
    enrichedData,
    inputData,
    pendingEnrichmentData,
    isLoading,
    validationErrors,
    isUserInternal,
    crossBrowserDirty,
    canReloadSession,
    canReset: canResetOrClone,
    canClone: canResetOrClone,
    isNotEditable,
    isPerformingEnrichment,
    isTabFrozen,
    negotiationId,
  };
};

function mapDispatchToProps(dispatch: ThunkDispatch, { formId }: DynamicFormOwnProps): DynamicFormDispatchProps {
  return {
    setFieldValue: (key: string, value: unknown) => {
      dispatch(formInputDataChangedThunk(formId, formId, key, value as Readonly<unknown>));
    },
    onResetTabClicked: () => {
      dispatch(workspaceResetTabRequested(formId, 'dynamic'));
    },
    onRefreshScheduleClicked: () => {
      dispatch(refreshScheduleThunk(formId, formId));
    },
    onRefreshDefaultDatesClicked: () => {
      dispatch(resetDefaultDatesThunk(formId, formId));
    },
    onTabCloned() {
      dispatch(workspaceTabClonedThunk(formId, 'dynamic', 'quote'));
    },
    onRollback(negotiationId) {
      dispatch(dynamicFormTabRollbackedThunk(formId, negotiationId));
    },
  };
}

const connectProductModificationForm = connect(mapStateToProps, mapDispatchToProps);

export const ProductModificationDynamicForm = connectProductModificationForm(RawDynamicForm);
